/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React, {useState} from "react";
import classes from "@/app/components/brands/BrandsListView/brandsListView.module.scss";
import {useTranslation} from "next-i18next";
import {useAppSelector} from "@/app/hooks/redux";
import {TBrandShortItem} from "@/app/types/brands/brandTypes";
import Breadcrumbs from "@/app/components/breadcrumbs/Breadcrumbs";
import {
    BRAND_DETAILS_PAGE_PREFIX,
    BRAND_FILTER_LABEL_ALL,
    brandsConfig
} from "@/app/config/brandsConfig";
import {TextField} from "@mui/material";
import Image from "next/image";
import SearchSvg from "@/app/components/assets/common/SearchSvg";

const filters = brandsConfig.defaultValues.filters;
type TBrandGroup = {
    label: string,
    brands: TBrandShortItem[],
}

const filterAndSortBrandsSingleGroup = (brands: TBrandShortItem[], selectedFilterLabel: string) => {

    const selectedFilter = filters.find((filter) => filter.label === selectedFilterLabel);

    if (!selectedFilter || selectedFilter.regexp === '') {
        // If "ALL" is selected or no filter is found, return all brands sorted by sort_order
        return brands.sort((a, b) => Number(a.sort_order) - Number(b.sort_order));
    }

    const regexp = new RegExp(selectedFilter.regexp);

    const filteredBrands = brands.filter((brand) => regexp.test(brand.brand_name ?? ''));

    return filteredBrands.sort((a, b) => Number(a.sort_order) - Number(b.sort_order));
};

// Function to group and sort brands
const groupAndSortBrands = (brands: TBrandShortItem[]) => {
    return filters.filter(filter => filter.regexp !== '').map((filter) => {
        let filteredBrands: TBrandShortItem[];

        const regexp = new RegExp(filter.regexp);
        filteredBrands = brands.filter((brand) => regexp.test(brand.brand_name ?? ''));

        // Sort filtered brands by sort_order
        filteredBrands.sort((a, b) => Number(a.sort_order) - Number(b.sort_order));

        return {
            label: filter.label,
            brands: filteredBrands,
        };
    });
};

const BrandsListView = (props: {
    brands: TBrandShortItem[],
    className?: string
}) => {
    const {t} = useTranslation();
    const {storeConfig} = useAppSelector(state => state.storeConfigReducer);
    const title = storeConfig?.brand_listing_page_title ?? brandsConfig.defaultValues.brandsListTitle;
    const showLogo = storeConfig?.brand_listing_brand_logo_show ? storeConfig?.brand_listing_brand_logo_show === "1" : brandsConfig.defaultValues.brandListShowBrandLogo;
    const showName = storeConfig?.brand_listing_brand_name_show ? storeConfig?.brand_listing_brand_name_show === "1" : brandsConfig.defaultValues.brandListShowBrandNane;
    const [search, setSearch] = useState('');
    const [selectedGroup, setSelectedGroup] = useState(BRAND_FILTER_LABEL_ALL);

    // Filter brands by search response
    const brands = search !== '' ?
        props.brands.filter((brand) => brand.brand_name?.toLowerCase().includes(search.toLowerCase())) :
        props.brands;
    let brandsFiltered:TBrandGroup[] = [];
    if(selectedGroup && selectedGroup !== BRAND_FILTER_LABEL_ALL) {
        // Filter brands by selected group
        brandsFiltered = [{
            label: selectedGroup,
            brands: filterAndSortBrandsSingleGroup(brands, selectedGroup)
        }];
    } else {
        // Break brands by groups and remove empty groups
        brandsFiltered = groupAndSortBrands(brands).filter(group => group.brands.length > 0);
    }

    const searchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    }

    const filterLabelClickHandler = (label:string) => {
        setSelectedGroup(label);
    }

    return <div className={`${classes.wrapper} ${props.className ?? ''}`}>
        <Breadcrumbs currentPageTitle={title} />
        <h1 className={classes.title}>{title}</h1>
        <div className={classes.toolbar}>
            <span className={classes.filterHeading}>{t('Filter brands')}</span>
            <div className={classes.filterList}>
                {filters.map((item) => {
                    return <span key={item.label}
                                 className={`${item.label === selectedGroup ? classes.active : ''}`}
                                 onClick={filterLabelClickHandler.bind(null, item.label)}>{item.label}</span>
                })}
            </div>
            <div className={classes.filterSearch}>
                <TextField placeholder={t('Search Brands')} value={search} onChange={searchChangeHandler} />
                <SearchSvg />
            </div>
        </div>
        <div className={classes.brands}>
            {brandsFiltered.map((brandsGroup) => {
                return <div key={brandsGroup.label} className={classes.group}>
                    <div className={classes.label}><span>{brandsGroup.label}</span></div>
                    <div className={classes.list}>{brandsGroup.brands.map((brand) => {
                        return <a key={brand.brand_name} href={`/${BRAND_DETAILS_PAGE_PREFIX}/${brand.url_slug}`} className={classes.brand}>
                            {showLogo && <span className={classes.imgWrap}>
                                {brand.logo ? <Image
                                    src={brand.logo}
                                    fill={true}
                                    className={classes.img}
                                    alt={brand.logo_alt ?? brand.brand_name ?? ''}/>
                                    : <div className={classes.noLogo}>{`${brand.brand_name}`}<br/>({t("No logo")})</div>}
                            </span>}
                            {showName && <span className={classes.name}>{brand.brand_name}</span>}
                        </a>
                    })}</div>
                </div>
            })}
        </div>
    </div>
}

export default BrandsListView;